import axios from 'axios';
import {  SERVICE_URL } from '@/store/_commons'
import getReCaptchaHeaderTBR from '@/utils/recaptchaTBR'
import { add, format } from 'date-fns';

// ----------------------------
// Slots

const namespaced = true;

const state = {
  DEFAULT_SELECT_OPTION: 'all',
};

const mutations = {};

const actions = {
  /**
   * Gets all available slots based on service type.
   * @param context
   */
  async getSlots({ dispatch, rootState }) {
    const endpoint = `${SERVICE_URL}reservation/slots`;

    try {
      const result = await axios.get(endpoint, { params: { visitTypeId: rootState.user.resType } });
      const reservationSlots = result.data.reservationSlots; 
      // "real" list - save for filtering
      dispatch('user/setStoreProperty', { property: 'availableSlotsByVisitType', value: reservationSlots }, { root: true });

      // list for display with filters
      dispatch('user/setStoreProperty', { property: 'resAvailableSlots', value: reservationSlots }, { root: true });    
    } catch (error) {
      console.error(error);
    }
  },

  /**
   * Holds a reservation slot for x minutes starting from the time when users enter their info.
   * @param context
   */
  /**
   * Holds a reservation slot for x minutes starting from the time when users enter their info.
   * @param {object} context - The Vuex context object.
   * @param {boolean} isExtendSlot - Indicates if the slot hold is being extended.
   */
  async createSlotHold(context, isExtendSlot) {
    const { dispatch, rootState } = context;
    const endpoint = `${SERVICE_URL}reservation/slot`;
    try {
      const config = await getReCaptchaHeaderTBR('post', endpoint);

      let headers = null;
      if(rootState.user.onlineToken != '' && rootState.user.onlineToken != null) {
        headers = {
          'headers': {
            'Recaptcha': config.headers.Recaptcha,
            'X-Access-Token': rootState.user.onlineToken
          }
        }
      }
      else {
        headers = config
      }

      const result = await axios.post(`${endpoint}/${rootState.user.resSlot}/${rootState.user.reservationSlotId}/${isExtendSlot}`, null,  headers );

      const endTime = new Date().getTime() + result.data.slotHoldMinutes * 60000;
      dispatch('user/setStoreProperty', { property: 'reservationSlotId', value: result.data.reservationSlotId }, { root: true });

      if (!isExtendSlot) {
        dispatch('user/setStoreProperty', { property: 'resSlotHoldEndTime', value: endTime }, { root: true });
        dispatch('user/setStoreProperty', { property: 'customerDetail', value: result.data.customerDetail }, { root: true });
        dispatch('user/setStoreProperty', { property: 'reservationUserId', value: result.data.reservationUserId }, { root: true });
      }
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  },
  
  /**
   * Removes a hold on a reservation slot.
   * @param context
   */
  async removeSlotHold(context) {
    const { dispatch, rootState } = context;
    const endpoint = `${SERVICE_URL}reservation/slot`;

    if (rootState.user.reservationSlotId > 0) {
      try {
        const config = await getReCaptchaHeaderTBR('delete', endpoint);
        const result = await axios.delete(`${endpoint}/${rootState.user.reservationSlotId}/${rootState.user.reservationUserId}`, config);
        await dispatch('user/setStoreProperty', { property: 'reservationSlotId', value: -1 }, { root: true });
        await dispatch('user/setStoreProperty', { property: 'reservationUserId', value: '' }, { root: true });
        return result; 
      } catch (error) {
        console.error('removeSlotHold', error);
      }
    }
    // Return a resolved promise if the condition is not met
    return;
  },

  // ---------------------------------------
  // Reservation Slot Filters (no API calls)
  /**
   * Computes the number of days (one day per column) for optimal display based on viewport width.
   * @returns {number}
   */
  getNumOfColumns() {
    let xDays = 6;
    const width = window.innerWidth;
    if (width <= 740) {
      xDays = 3;
    }
    return xDays;
  },

  /**
   * Multiple layers of data-massaging for proper frontend display (as data returned by backend is not usable as-is).
   * @param {object} context - The Vuex context object.
   * @param {object} params - The filtering parameters.
   * @returns {Promise<void>}
 */
  async filterSlots(context, params) {
    const { dispatch, state, rootState } = context;

    const data = params ? params : {  
      location: state.DEFAULT_SELECT_OPTION,
      time: state.DEFAULT_SELECT_OPTION,
      week: 0,
      page: 0,
      byXDays: false
    };

    // remove empty "weeks", if needed
    const cleaned = await dispatch('cleanWeeks', rootState.user.availableSlotsByVisitType);

    // re-apply all filters to _all available slots_ to simulate db query IN THE FOLLOWING ORDER
    // (by days pagination or by week, by location, then by time) ~
    let sliced = [];
    // mutually exclusive: by week or by x number of days
    if (data.byXDays) {
      sliced = await dispatch('getSlotsBySlice', { slots: cleaned, batchIndex: data.page });
    } else {
      sliced = await dispatch('getSlotsBySlice', { slots: cleaned, batchIndex: data.week });
    }

    const locList = await dispatch('getSlotsByLocation', { slots: sliced, location: data.location });
    const timeList = await dispatch('getSlotsByTime', { slots: locList, time: data.time });

    // Re-render desired days by dispatching the root action with `{ root: true }`
    await dispatch('user/setStoreProperty', { property: 'resCurrentDays', value: timeList }, { root: true });
  },
  
  /**
   * Removes initial week(s) from _all available slots_ if the full week(s) are empty.
   * @param {object} context - The Vuex context object.
   * @param {Array} list - The availableSlotsByVisitType state variable.
   * @returns {Promise<Array>}
   */
  async cleanWeeks(context, list) {
    const { dispatch } = context;

    const weekdays = await dispatch('getSlotsPerWeekday', { slots: list });

    let firstSlottedWeekIndex = -1;
    const numOfFullWeeks = Math.floor(weekdays.length / 6);

    for (let k = 0; k < numOfFullWeeks; k++) {
      const weekSlice = weekdays.slice(k * 6, (k + 1) * 6);

      // Check if any day in the current week has available slots
      const hasAvailableSlot = weekSlice.some(day =>
        day.some(slot => slot.availableSlotCount > 0)
      );

      if (hasAvailableSlot) {
        firstSlottedWeekIndex = k;
        break; // Exit the loop once the first slotted week is found
      }
    }

    // If a slotted week was found, set the weeks in the root state
    if (firstSlottedWeekIndex !== -1) {
      const uniqueWeeks = await dispatch('getUniqueWeeks', { currentWeek: firstSlottedWeekIndex });
      dispatch('user/setStoreProperty', { property: 'resSlottedWeeks', value: uniqueWeeks }, { root: true });
      
      // Return the list starting from the first slotted week
      return weekdays.slice(firstSlottedWeekIndex * 6);
    }

    // If no slotted weeks were found, return an empty array or the original list.
    // The original logic implies returning an empty array if all weeks are empty.
    return [];
  },

  /**
   * Filter available slots by user-selected location.
   * @param context
   * @param data
   * @returns {*|default.watch.availableSlotsByVisitType|[]}
   */
  async getSlotsByLocation(context, data) {
    const { state, rootState } = context;
    let slots = data.slots || rootState.user.availableSlotsByVisitType;
    const location = data.location;

    if (location !== state.DEFAULT_SELECT_OPTION) {
      slots = slots.map((day) => {
        return day.filter(item => (item.location === location || item.location === ''));
      });
    }
    return slots;
  },

  /**
   * Filters available slots by user-selected time (AM or PM).
   * @param context
   * @param data
   * @returns {Promise<*|default.watch.availableSlotsByVisitType|[]>}
   */

  /**
   * Filters available slots by user-selected time (AM or PM).
   * @param {object} context - The Vuex context object.
   * @param {object} data - The filtering parameters.
   * @returns {Promise<Array>}
  */
  async getSlotsByTime(context, data) {
    const { dispatch, state, rootState } = context;
    let slots = data.slots || rootState.user.availableSlotsByVisitType;
    const time = data.time;

    if (time !== state.DEFAULT_SELECT_OPTION) {
      slots = await dispatch('getSlotsByTimeUtil', { isMorning: time === 'AM', slots: slots });
    }

    // Ensure this dispatch is awaited
    slots = await dispatch('fixMissingDayByTime', { slots: slots });

    return slots;
  },

  /**
   * Determines a given "time" as AM or PM.
   * @param {object} context - The Vuex context object.
   * @param {object} data - The filtering parameters.
   * @returns {Array}
  */
  async getSlotsByTimeUtil(context, data) {
    const list = data.slots.map((day) => {
      return day.filter(item => {
        // Use nullish coalescing to provide a default empty string if item.startTime is undefined
        const hr = Number((item.startTime ?? '').substr(0, 2));
        if (data.isMorning) {
          if (hr < 12) {  // AM - already include 0 hr
            return item;
          }
        } 
        else if (hr > 11 || hr === 0) {   // PM
          return item;
        }
      });
    });
    return list;
  },
  
  /**
  * Plugs in a missing day hole caused by the _time of day_ filter (AM vs. PM).
  * This cannot be fixed on the REST API side since the API is only plugging in missing days
  * by _full day_ and there is no sensible way to differentiate AM vs. PM based on startTime.
  * @param {object} context - The Vuex context object.
  * @param {object} data - The filtering parameters.
  * @returns {Array} The corrected week array.
  */
  async fixMissingDayByTime(context, data) {
    const week = data.slots;

    // Find the index of the first day with no slots.
    const firstEmptyDayIndex = week.findIndex(day => day.length === 0);

    if (firstEmptyDayIndex > -1) {
      let missingDate;
      if (firstEmptyDayIndex === 0) {
        // Find the index of the first available day after the empty day.
        const firstAvailableDayIndex = week.findIndex(day => day.length > 0);

        // If a day with slots is found, calculate the missing date.
        if (firstAvailableDayIndex > -1) {
          const firstAvailableDate = new Date(week[firstAvailableDayIndex][0].scheduleDate);
          missingDate = format(add(firstAvailableDate, { days: -firstAvailableDayIndex }), 'yyyy-MM-dd');
        } else {
          // If the entire week is empty, we can't calculate the date. Return as is.
          // This case should be handled higher up, e.g., in `cleanWeeks`.
          return week;
        }
      } else {
        // Calculate the missing date based on the preceding day.
        const previousDate = new Date(week[firstEmptyDayIndex - 1][0].scheduleDate);
        missingDate = format(add(previousDate, { days: 1 }), 'yyyy-MM-dd');
      }

      // Plug in the missing day with a placeholder empty slot.
      if (missingDate) {
        week[firstEmptyDayIndex].push({
          availableSlotCount: 0,
          scheduleDate: `${missingDate}T00:00:00`,
        });
      }
    }

    return week;
  },


  /**
   * Sets the desired number of days for display "within" a selected week.
   * Number of days are determined by viewport width while a "week" always contains six days.
   * @param {object} context - The Vuex context object.
   * @param {object} data
   *    slots: The list of available slots
   *    batchIndex: Either the week index or page index. Defaults to 0.
   * @returns {Promise<Array>} The sliced list of available slots.
  */
  async getSlotsBySlice(context, data) {
    const { dispatch, rootState } = context;

    const slots = (data && data.slots) || rootState.user.availableSlotsByVisitType;
    const batchIndex = data.batchIndex || 0; // week or page index

    const slotsByWeekday = Array.isArray(slots) && slots.length > 0 ? await dispatch('getSlotsPerWeekday', { slots }) : [];


    const xDays = await dispatch('getNumOfColumns');

    const start = xDays * batchIndex;
    const end = start + xDays;

    let list = [];
    if (Array.isArray(slotsByWeekday)) {
      list = slotsByWeekday.length === 1 ? slotsByWeekday[0] : slotsByWeekday;
    }

    const isLastPage = end >= list.length;

    // Dispatch the update for resIsLastPage.
    await new Promise(resolve => setTimeout(resolve, 750));
    
    dispatch('user/setStoreProperty', { property: 'resIsLastPage', value: isLastPage }, { root: true });

    return list.slice(start, end);
  },

  /**
    * "Formats" the data into manageable lists - a list presents a calendar day.
    * Each day has its own list of available reservation slots. Days that do not have open slots
    * have an array of one element--PER LOCATION--with scheduleDate (as usual) and startTime=00:00:00.
    * @param {object} context - The Vuex context object.
    * @param {object} data - The filtering parameters.
    * @returns {Array<Array<object>>} A nested array of slots, grouped and sorted by date.
  */
  async getSlotsPerWeekday(context, data) {
    const slots = (data && data.slots)  || context.rootState.user.availableSlotsByVisitType;

      // Add a guard to ensure slots is a valid array
    if (!Array.isArray(slots) || slots.length === 0) {
      return []; // Return an empty array if there are no slots
    }

    // 1. Sort by `scheduleDate`, `startTime`, and `location`
    const sortedSlots = slots.slice().sort((a, b) => {
      // Primary sort: scheduleDate
      if (a.scheduleDate < b.scheduleDate) return -1;
      if (a.scheduleDate > b.scheduleDate) return 1;

      // Secondary sort: startTime
      if (a.startTime < b.startTime) return -1;
      if (a.startTime > b.startTime) return 1;

      // Tertiary sort: location
      if (a.location < b.location) return -1;
      if (a.location > b.location) return 1;

      return 0;
    });

    // 2. Group by `scheduleDate` using a `reduce` function
    const groupedByDate = sortedSlots.reduce((acc, item) => {
      const key = item.scheduleDate;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});

    // 3. Convert the grouped object to an array of arrays and return
    // The groups are already sorted correctly because the original array was sorted.
    return Object.values(groupedByDate);
  },

  /**
   * Divides available slots into 6‑day weeks.
   * @param {object} context - The Vuex context object.
   * @param {object} data - The current week index.
   * @returns {Array<object>} An array of objects representing each week's date range.
   */
  async getUniqueWeeks(context, data) {
    const { rootState } = context;
    const availableSlotsByVisitType = rootState.user.availableSlotsByVisitType;

    if (!Array.isArray(availableSlotsByVisitType) || availableSlotsByVisitType.length === 0) {
      return [];
    }

    // Group slots by date
    const days = availableSlotsByVisitType
      .slice()  // Create a shallow copy to avoid mutating the original state
      .sort((a, b) => a.scheduleDate.localeCompare(b.scheduleDate))
      .reduce((acc, slot) => {
        const date = slot.scheduleDate;
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(slot);
        return acc;
      }, {});

    const daysArray = Object.values(days);
    if (daysArray.length === 0) return [];

    const numOfWeeks = Math.ceil(daysArray.length / 6); // "weeks" are always in multiple of M-Sat
    const uniqueWeeks = [];

    for (let i = 0; i < numOfWeeks; i++) {
      const startIndex = i * 6;
      const endIndex = startIndex + 5;

      const startDay = daysArray[startIndex]?.[0]?.scheduleDate;
      const endDay = daysArray[endIndex]?.[0]?.scheduleDate;

      if (startDay && endDay) {
        uniqueWeeks.push({ from: startDay, to: endDay });
      }
    }

    const current = data.currentWeek;
    return current > 0 ? uniqueWeeks.slice(current) : uniqueWeeks;
  }
};


const getters = {
  slotsStoreData: state => ({
    defaultSelectOption: state.DEFAULT_SELECT_OPTION,
  }),
};

export default {
  namespaced,
  state,
  mutations,
  actions,
  getters,
}
